import { useEffect, useState } from 'react';

/**
 * Preloads one or more images and reports back once they're all
 * decoded and ready to paint instantly. Used anywhere an animation
 * (the Hero glitch, a page banner's zoom-in) depends on an image
 * being fully downloaded first -- without this, the first paint on a
 * cold cache can run the animation against a still-loading image.
 *
 * Never blocks forever: a failed image still counts as "settled" so
 * a broken URL can't hang the page indefinitely.
 */
export default function useImagePreload(sources) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const list = (Array.isArray(sources) ? sources : [sources]).filter(Boolean);

    if (list.length === 0) {
      setLoaded(true);
      return;
    }

    setLoaded(false);
    let cancelled = false;

    const promises = list.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve;
          img.src = src;
        })
    );

    Promise.all(promises).then(() => {
      if (!cancelled) setLoaded(true);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(sources)]);

  return loaded;
}
